import { type ToolSpec, n, s, fmt } from "./types";

const ACTIVITY = [
  { value: "1.2", label: "Sedentary — desk job, no exercise" },
  { value: "1.375", label: "Light — 1-3 workouts / week" },
  { value: "1.55", label: "Moderate — 3-5 workouts / week" },
  { value: "1.725", label: "Heavy — 6-7 workouts / week" },
  { value: "1.9", label: "Athlete — 2x daily or physical job" },
];

export const FITNESS_TOOLS: ToolSpec[] = [
  {
    slug: "tdee-calculator-weight-loss",
    cluster: "fitness",
    name: "TDEE & Macro Calculator",
    h1: "TDEE Calculator for Weight Loss Target Date (Accurate Daily Macro Breakdown)",
    title: "TDEE Calculator for Weight Loss Target Date & Macros",
    description:
      "Free TDEE calculator using Mifflin-St Jeor and Katch-McArdle. Get maintenance calories, a cutting target, protein/carb/fat grams and the exact date you hit your goal weight.",
    keywords: [
      "tdee calculator for weight loss target date",
      "how to calculate tdee manually for cutting",
      "tdee calculator metric and imperial body fat",
      "mifflin st jeor tdee formula vs katch mcardle",
      "tdee macro split for muscle maintenance",
      "daily calorie deficit to lose 2 lbs a week",
    ],
    inputs: [
      { id: "units", label: "Unit system", type: "select", def: "metric", options: [{ value: "metric", label: "Metric (kg / cm)" }, { value: "imperial", label: "Imperial (lb / in)" }] },
      { id: "gender", label: "Gender", type: "select", def: "male", options: [{ value: "male", label: "Male" }, { value: "female", label: "Female" }] },
      { id: "age", label: "Age", type: "number", def: "30", unit: "years", min: 14, max: 90 },
      { id: "weight", label: "Body weight", type: "number", def: "80", unit: "kg / lb", min: 30 },
      { id: "height", label: "Height", type: "number", def: "175", unit: "cm / in", min: 40 },
      { id: "bodyfat", label: "Body fat % (optional)", type: "number", def: "", unit: "%", min: 3, max: 65, help: "Filled = Katch-McArdle engine, blank = Mifflin-St Jeor." },
      { id: "activity", label: "Activity level", type: "select", def: "1.375", options: ACTIVITY },
      { id: "goal", label: "Weight goal", type: "select", def: "-500", options: [
        { value: "0", label: "Maintenance (0 kcal)" },
        { value: "-250", label: "Mild cut (-250 kcal)" },
        { value: "-500", label: "Moderate cut (-500 kcal)" },
        { value: "-1000", label: "Aggressive cut (-1000 kcal)" },
      ] },
      { id: "target", label: "Goal weight", type: "number", def: "72", unit: "kg / lb", min: 30 },
    ],
    compute: (v) => {
      const imperial = s(v, "units") === "imperial";
      const kg = imperial ? n(v, "weight") * 0.453592 : n(v, "weight");
      const cm = imperial ? n(v, "height") * 2.54 : n(v, "height");
      const goalKg = imperial ? n(v, "target") * 0.453592 : n(v, "target");
      const bf = n(v, "bodyfat");
      const male = s(v, "gender") === "male";
      const bmr = bf > 0
        ? 370 + 21.6 * (kg * (1 - bf / 100))
        : 10 * kg + 6.25 * cm - 5 * n(v, "age") + (male ? 5 : -161);
      const tdee = bmr * n(v, "activity", 1.375);
      const deficit = n(v, "goal");
      const target = Math.max(1000, tdee + deficit);
      const protein = Math.round((imperial ? n(v, "weight") : kg * 2.20462) * 1);
      const fat = Math.round((target * 0.27) / 9);
      const carbs = Math.max(0, Math.round((target - protein * 4 - fat * 9) / 4));
      const lossKg = Math.max(0, kg - goalKg);
      const weeklyKg = (Math.abs(deficit) * 7) / 7700;
      const weeks = weeklyKg > 0 ? lossKg / weeklyKg : 0;
      const date = new Date(Date.now() + weeks * 7 * 86400000);
      return {
        cards: [
          { label: "Maintenance TDEE", value: `${fmt(tdee)} kcal`, sub: bf > 0 ? "Katch-McArdle engine" : "Mifflin-St Jeor engine", accent: true },
          { label: "Daily target", value: `${fmt(target)} kcal`, sub: `${deficit === 0 ? "maintenance" : `${deficit} kcal/day`}` },
          { label: "BMR (at rest)", value: `${fmt(bmr)} kcal`, sub: "Zero-activity baseline" },
          { label: "Goal date", value: weeks > 0 ? date.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }) : "Already there", sub: weeks > 0 ? `${fmt(weeks, 1)} weeks away` : "No deficit needed" },
        ],
        bars: [
          { label: "Protein", value: protein * 4, display: `${protein} g` },
          { label: "Carbs", value: carbs * 4, display: `${carbs} g` },
          { label: "Fat", value: fat * 9, display: `${fat} g` },
        ],
        summary: `At ${fmt(target)} kcal per day you lose roughly ${fmt(weeklyKg, 2)} kg (${fmt(weeklyKg * 2.205, 2)} lb) per week.`,
      };
    },
    intro:
      "Total Daily Energy Expenditure is the number of calories your body burns in 24 hours once resting metabolism, digestion (TEF), non-exercise movement (NEAT) and training are added together. This calculator runs both accepted engines — Mifflin-St Jeor when you only know age, height and weight, and Katch-McArdle when you supply a body fat percentage, because lean mass predicts resting burn far better than total mass does.",
    sections: [
      { h: "How to calculate TDEE manually for cutting", body: "Start with BMR = 10×weight(kg) + 6.25×height(cm) − 5×age + 5 (men) or −161 (women). Multiply by your honest activity factor: 1.2 sedentary, 1.375 light, 1.55 moderate, 1.725 heavy, 1.9 athlete. Subtract the deficit that suits your timeline — 500 kcal a day is the classic one-pound-a-week cut because a pound of body fat stores roughly 3,500 kcal." },
      { h: "Mifflin-St Jeor vs Katch-McArdle", body: "Mifflin-St Jeor is the general-population standard and lands within about 10% of measured expenditure for most adults. Katch-McArdle (370 + 21.6 × lean mass in kg) ignores fat mass entirely, so it is more accurate for lean athletes, very obese individuals and anyone with a DEXA or calliper reading. Below 15% body fat the two formulas can differ by 200+ kcal a day." },
      { h: "Adaptive thermogenesis vs baseline metabolism", body: "Baseline metabolism is what your organs and lean tissue cost simply to stay alive. Adaptive thermogenesis is the extra downshift that happens during a prolonged deficit: NEAT drops, thyroid output falls, movement becomes unconsciously more efficient. A 10-12 week cut typically loses 5-8% more energy expenditure than the arithmetic predicts, which is why recalculating TDEE every 4 kg of loss beats trusting one number all year." },
      { h: "Macro split for muscle maintenance", body: "Protein at roughly 1 g per pound of body weight protects lean tissue in a deficit; fat at about 25-30% of total calories keeps hormones stable; carbohydrates fill the rest and fuel training intensity. The bar chart above shows your split by calories, with grams printed on each bar." },
    ],
    staticTable: {
      title: "BMR vs TDEE by activity tier (reference)",
      caption: "Mifflin-St Jeor at 175 cm / 80 kg (male) and 163 cm / 65 kg (female).",
      head: ["Age", "Male BMR", "Male TDEE @1.375", "Male TDEE @1.725", "Female BMR", "Female TDEE @1.375", "Female TDEE @1.725"],
      rows: [
        [20, 1780, 2447, 3070, 1373, 1888, 2368],
        [30, 1730, 2379, 2984, 1323, 1819, 2282],
        [40, 1680, 2310, 2898, 1273, 1750, 2196],
        [50, 1630, 2241, 2812, 1223, 1682, 2110],
        [60, 1580, 2172, 2726, 1173, 1613, 2023],
      ],
    },
    faqs: [
      { q: "How does this TDEE calculator estimate my exact weight loss target date?", a: "It calculates your baseline Total Daily Energy Expenditure and subtracts your selected calorie deficit. Since one pound of fat stores roughly 3,500 calories, a 500-calorie daily deficit produces about one pound of loss a week, and the tool projects that rate forward to your goal weight to print a target date." },
      { q: "Which formula is more accurate: Mifflin-St Jeor or Katch-McArdle?", a: "If you know your body fat percentage or lean body mass, Katch-McArdle is more accurate because lean tissue burns significantly more energy than fat tissue. Without a body composition reading, Mifflin-St Jeor is the industry standard and the safer default." },
      { q: "Why am I not losing weight while eating at my calculated deficit?", a: "The usual causes are uncounted calories from oils, sauces and drinks, overstating your activity level, water retention from a new training block or high sodium, and metabolic adaptation after several months of dieting." },
      { q: "How big a deficit is safe?", a: "Most guidelines cap sustainable loss at 0.5-1% of body weight per week. Aggressive 1,000 kcal deficits are usually reserved for higher starting body weights and short blocks, because they accelerate lean mass loss and appetite rebound." },
    ],
    related: ["calorie-deficit-target-date-calculator", "ideal-body-weight-calculator", "us-navy-body-fat-calculator", "keto-net-carb-calculator"],
  },

  {
    slug: "us-navy-body-fat-calculator",
    cluster: "fitness",
    name: "Body Fat % (US Navy Tape Method)",
    h1: "US Navy Body Fat Calculator with Tape Measure (Circumference Method)",
    title: "US Navy Body Fat Calculator with Tape Measure",
    description:
      "Estimate body fat percentage with the US Navy circumference formula. Enter neck, waist and hip measurements to get body fat %, lean mass, fat mass and Navy standard pass/fail.",
    keywords: [
      "us navy body fat calculator with tape measure",
      "navy body fat standards by age chart",
      "how to measure waist and neck for body fat",
      "body fat percentage from circumference formula",
    ],
    inputs: [
      { id: "gender", label: "Gender", type: "select", def: "male", options: [{ value: "male", label: "Male" }, { value: "female", label: "Female" }] },
      { id: "age", label: "Age", type: "number", def: "30", unit: "years", min: 17, max: 80 },
      { id: "height", label: "Height", type: "number", def: "175", unit: "cm", min: 120 },
      { id: "weight", label: "Weight", type: "number", def: "80", unit: "kg", min: 30 },
      { id: "neck", label: "Neck circumference", type: "number", def: "38", unit: "cm", min: 20 },
      { id: "waist", label: "Waist circumference", type: "number", def: "88", unit: "cm", min: 40 },
      { id: "hip", label: "Hip circumference (women)", type: "number", def: "96", unit: "cm", min: 40 },
    ],
    compute: (v) => {
      const male = s(v, "gender") === "male";
      const h = n(v, "height"), neck = n(v, "neck"), waist = n(v, "waist"), hip = n(v, "hip");
      const bf = male
        ? 495 / (1.0324 - 0.19077 * Math.log10(Math.max(1, waist - neck)) + 0.15456 * Math.log10(Math.max(1, h))) - 450
        : 495 / (1.29579 - 0.35004 * Math.log10(Math.max(1, waist + hip - neck)) + 0.221 * Math.log10(Math.max(1, h))) - 450;
      const pct = Math.min(70, Math.max(2, bf));
      const w = n(v, "weight");
      const fatMass = (w * pct) / 100;
      const age = n(v, "age");
      const limit = male ? (age < 21 ? 22 : age < 28 ? 24 : age < 40 ? 26 : 28) : (age < 21 ? 33 : age < 28 ? 34 : age < 40 ? 36 : 38);
      const cat = male
        ? pct < 6 ? "Essential" : pct < 14 ? "Athletic" : pct < 18 ? "Fitness" : pct < 25 ? "Average" : "Above average"
        : pct < 14 ? "Essential" : pct < 21 ? "Athletic" : pct < 25 ? "Fitness" : pct < 32 ? "Average" : "Above average";
      return {
        cards: [
          { label: "Body fat", value: `${fmt(pct, 1)}%`, sub: cat, accent: true },
          { label: "Fat mass", value: `${fmt(fatMass, 1)} kg` },
          { label: "Lean mass", value: `${fmt(w - fatMass, 1)} kg`, sub: "Drives Katch-McArdle BMR" },
          { label: "Navy standard", value: pct <= limit ? "PASS" : "OVER", sub: `Limit for your age: ${limit}%` },
        ],
        bars: [
          { label: "Fat mass", value: fatMass, display: `${fmt(fatMass, 1)} kg` },
          { label: "Lean mass", value: w - fatMass, display: `${fmt(w - fatMass, 1)} kg` },
        ],
        summary: `A ${fmt(pct, 1)}% reading puts you in the "${cat}" band; the Navy ceiling at age ${age} is ${limit}%.`,
      };
    },
    intro:
      "The US Navy circumference method estimates body fat from tape measurements instead of skinfold callipers or a DEXA scan. It works because fat deposits at the waist (and hips for women) scale predictably against neck girth and standing height, so a log-transformed ratio tracks hydrostatic weighing within roughly 3-4 percentage points for most adults.",
    sections: [
      { h: "How to take the measurements correctly", body: "Measure first thing in the morning, unclothed, without pulling the tape tight. Neck: just below the larynx, tape sloping slightly downward at the front. Waist (men): at the navel. Waist (women): at the narrowest point of the torso. Hips (women): at the widest point of the glutes. Take each measurement three times and average them — a single centimetre of tape error shifts the result by about 0.7 percentage points." },
      { h: "Why the formula uses logarithms", body: "Fat distribution is not linear with girth. The Hodgdon-Beckett equations apply base-10 logarithms to the waist-minus-neck difference and to height, which compresses the curve at the extremes and keeps very tall or very lean subjects from being over-estimated." },
      { h: "Where the tape method fails", body: "Heavily muscled lifters with thick necks read artificially lean, while people carrying visceral rather than subcutaneous fat can read artificially high. Post-pregnancy abdominal separation, bloating and a full bladder all inflate waist girth. For research-grade accuracy use DEXA or a four-site calliper protocol; use the tape for weekly trend tracking." },
    ],
    staticTable: {
      title: "US Navy maximum allowable body fat by age",
      head: ["Age band", "Male limit", "Female limit", "Typical athletic range (M)", "Typical athletic range (F)"],
      rows: [
        ["17-20", "22%", "33%", "8-14%", "16-21%"],
        ["21-27", "24%", "34%", "9-15%", "17-22%"],
        ["28-39", "26%", "36%", "10-17%", "18-24%"],
        ["40+", "28%", "38%", "12-19%", "20-26%"],
      ],
    },
    faqs: [
      { q: "How accurate is the US Navy body fat calculator?", a: "Against hydrostatic weighing it typically lands within 3-4 percentage points for average builds. It is less reliable for very muscular or very obese individuals because neck and waist girth stop tracking fat mass linearly." },
      { q: "Do women need the hip measurement?", a: "Yes. The female equation uses waist plus hip minus neck, because fat is distributed gluteofemorally as well as abdominally. Skipping the hip value makes the result meaningless." },
      { q: "What body fat percentage is healthy?", a: "Broadly, 10-20% for men and 18-28% for women are considered healthy ranges. Essential fat — the minimum needed for hormonal function — is roughly 3% in men and 12% in women." },
    ],
    related: ["tdee-calculator-weight-loss", "ideal-body-weight-calculator", "calorie-deficit-target-date-calculator", "target-heart-rate-zone-calculator"],
  },

  {
    slug: "one-rep-max-calculator",
    cluster: "fitness",
    name: "One Rep Max (1RM) Calculator",
    h1: "One Rep Max Calculator for Bench Press, Squat and Deadlift",
    title: "One Rep Max Calculator — Bench, Squat & Deadlift 1RM",
    description:
      "Estimate your one rep max from any set of 1-12 reps using Epley, Brzycki and Lombardi formulas, plus a full percentage training chart for bench press, squat and deadlift.",
    keywords: [
      "one rep max calculator bench press squat deadlift",
      "epley vs brzycki 1rm formula difference",
      "percentage of 1rm training chart 5x5",
      "how many reps at 80 percent of one rep max",
    ],
    inputs: [
      { id: "lift", label: "Lift", type: "select", def: "bench", options: [{ value: "bench", label: "Bench press" }, { value: "squat", label: "Back squat" }, { value: "deadlift", label: "Deadlift" }, { value: "ohp", label: "Overhead press" }] },
      { id: "weight", label: "Weight lifted", type: "number", def: "100", unit: "kg / lb", min: 1 },
      { id: "reps", label: "Repetitions completed", type: "number", def: "5", min: 1, max: 12 },
    ],
    compute: (v) => {
      const w = n(v, "weight"), r = Math.min(12, Math.max(1, n(v, "reps", 1)));
      const epley = r === 1 ? w : w * (1 + r / 30);
      const brzycki = w * (36 / (37 - r));
      const lombardi = w * Math.pow(r, 0.1);
      const avg = (epley + brzycki + lombardi) / 3;
      const pcts = [95, 90, 85, 80, 75, 70, 65, 60];
      return {
        cards: [
          { label: "Estimated 1RM", value: `${fmt(avg, 1)}`, sub: "3-formula average", accent: true },
          { label: "Epley", value: fmt(epley, 1), sub: "w × (1 + reps/30)" },
          { label: "Brzycki", value: fmt(brzycki, 1), sub: "w × 36/(37 − reps)" },
          { label: "Lombardi", value: fmt(lombardi, 1), sub: "w × reps^0.1" },
        ],
        table: {
          title: "Your training percentages",
          head: ["% of 1RM", "Working weight", "Typical rep target"],
          rows: pcts.map((p) => [`${p}%`, fmt((avg * p) / 100, 1), p >= 95 ? "1-2" : p >= 90 ? "2-3" : p >= 85 ? "3-5" : p >= 80 ? "5-6" : p >= 75 ? "6-8" : p >= 70 ? "8-10" : p >= 65 ? "10-12" : "12-15"]),
        },
        summary: `Estimates diverge more as reps rise — at ${r} reps the spread between formulas is ${fmt(Math.max(epley, brzycki, lombardi) - Math.min(epley, brzycki, lombardi), 1)} units.`,
      };
    },
    intro:
      "A one rep max is the heaviest load you can move for a single clean repetition. Testing it is fatiguing and technically risky, so lifters estimate it from a submaximal set. Every estimation formula is a curve fit to real lifting data, and each behaves differently at high rep counts — which is why this tool prints three of them side by side rather than pretending one number is exact.",
    sections: [
      { h: "Epley vs Brzycki vs Lombardi", body: "Epley is linear and slightly generous above 8 reps. Brzycki is conservative and considered the most reliable in the 2-8 rep window used by most strength programmes. Lombardi uses a power curve and tracks better for high-rep endurance sets. Below 6 reps all three agree within a couple of percent; at 12 reps they can disagree by 8-10%." },
      { h: "Why lift selection changes reliability", body: "Deadlifts have the flattest strength curve, so a 5-rep set predicts the single well. Bench press fatigues faster because of the smaller muscle mass and stretch-shortening contribution, and overhead press degrades fastest of all. Treat estimates from squats and deadlifts as tighter than estimates from pressing movements." },
      { h: "Programming from the percentage chart", body: "Classic linear programmes sit at 75-85% for volume work and touch 90%+ only in peaking blocks. 5×5 lands around 80%, a hypertrophy 4×8 around 72-75%, and speed work uses 50-60% with compensatory acceleration. Recalculate after each mesocycle rather than after each personal best." },
    ],
    staticTable: {
      title: "Reps-to-percentage reference (Brzycki basis)",
      head: ["Reps performed", "% of 1RM", "Multiplier to 1RM", "Primary adaptation"],
      rows: [
        [1, "100%", "1.00", "Maximal strength"],
        [2, "97%", "1.03", "Maximal strength"],
        [3, "94%", "1.06", "Strength"],
        [5, "89%", "1.13", "Strength"],
        [6, "86%", "1.16", "Strength / hypertrophy"],
        [8, "81%", "1.24", "Hypertrophy"],
        [10, "75%", "1.33", "Hypertrophy"],
        [12, "71%", "1.41", "Hypertrophy / endurance"],
      ],
    },
    faqs: [
      { q: "How many reps should I use for the most accurate 1RM estimate?", a: "Three to six reps taken close to technical failure gives the tightest estimate. Sets above eight reps are limited by local muscular endurance rather than maximal force, so the projection drifts high." },
      { q: "Is it safe to test a true one rep max?", a: "For trained lifters with stable technique and a spotter, occasional testing is fine. Beginners gain nothing from it — estimated maxes from a 5-rep set are accurate enough to programme with and far less fatiguing." },
      { q: "Why is my bench press estimate higher than what I can actually lift?", a: "Pressing strength decays faster with fatigue than the formulas assume, and a paused competition bench is typically 3-6% lower than a touch-and-go set." },
    ],
    related: ["tdee-calculator-weight-loss", "target-heart-rate-zone-calculator", "ideal-body-weight-calculator", "us-navy-body-fat-calculator"],
  },

  {
    slug: "keto-net-carb-calculator",
    cluster: "fitness",
    name: "Keto Net Carb & Macro Split",
    h1: "Keto Net Carb Calculator (Daily Macro Breakdown in Grams)",
    title: "Keto Net Carb Calculator — Daily Macro Breakdown",
    description:
      "Work out ketogenic net carbs, protein and fat grams from your daily calorie budget. Includes total vs net carb maths, fibre and sugar alcohol subtraction, and a keto tier table.",
    keywords: [
      "keto net carb calculator daily macro breakdown",
      "net carbs vs total carbs keto formula",
      "how many grams of fat per day on keto",
      "keto macro percentages 70 25 5",
    ],
    inputs: [
      { id: "calories", label: "Daily calorie budget", type: "number", def: "2000", unit: "kcal", min: 800 },
      { id: "carbpct", label: "Carb allowance", type: "select", def: "5", options: [
        { value: "5", label: "Strict keto — 5% carbs" },
        { value: "8", label: "Moderate keto — 8% carbs" },
        { value: "12", label: "Low carb — 12% carbs" },
        { value: "20", label: "Keto transition — 20% carbs" },
      ] },
      { id: "protein", label: "Protein target", type: "number", def: "130", unit: "g", min: 20 },
      { id: "fibre", label: "Daily fibre eaten", type: "number", def: "18", unit: "g", min: 0 },
      { id: "alcohols", label: "Sugar alcohols eaten", type: "number", def: "6", unit: "g", min: 0 },
    ],
    compute: (v) => {
      const cal = n(v, "calories"), carbPct = n(v, "carbpct", 5);
      const netCarb = (cal * (carbPct / 100)) / 4;
      const protein = n(v, "protein");
      const fat = Math.max(0, (cal - netCarb * 4 - protein * 4) / 9);
      const total = netCarb + n(v, "fibre") + n(v, "alcohols") * 0.5;
      return {
        cards: [
          { label: "Net carb limit", value: `${fmt(netCarb)} g`, sub: `${carbPct}% of calories`, accent: true },
          { label: "Fat target", value: `${fmt(fat)} g`, sub: `${fmt((fat * 9 * 100) / cal)}% of calories` },
          { label: "Protein", value: `${fmt(protein)} g`, sub: `${fmt((protein * 4 * 100) / cal)}% of calories` },
          { label: "Total carbs on label", value: `${fmt(total)} g`, sub: "Net + fibre + half of alcohols" },
        ],
        bars: [
          { label: "Fat", value: fat * 9, display: `${fmt(fat)} g` },
          { label: "Protein", value: protein * 4, display: `${fmt(protein)} g` },
          { label: "Net carbs", value: netCarb * 4, display: `${fmt(netCarb)} g` },
        ],
        summary: `You can eat about ${fmt(total)} g of labelled carbohydrate a day and still land on ${fmt(netCarb)} g net.`,
      };
    },
    intro:
      "Net carbs are the carbohydrates that actually raise blood glucose: total carbohydrate minus fibre, minus roughly half the weight of most sugar alcohols. Nutritional ketosis usually begins when net intake drops under 50 g a day and deepens under 20-25 g, which is why the calculator separates the label number from the number your metabolism responds to.",
    sections: [
      { h: "Total carbs vs net carbs", body: "Fibre passes through the small intestine undigested, so it contributes no usable glucose and is subtracted in full. Sugar alcohols vary: erythritol is subtracted entirely, while maltitol raises glucose enough that only about half its weight should be deducted. Allulose and glycerin behave like erythritol for most people." },
      { h: "Why protein is entered, not derived", body: "Protein needs are set by lean mass and training, not by the ketogenic ratio. Fixing protein first and letting fat absorb the remaining calories prevents the common mistake of under-eating protein to hit an arbitrary 75% fat target — muscle loss on keto is almost always a protein problem, not a fat problem." },
      { h: "Electrolytes during induction", body: "Falling insulin makes the kidneys excrete sodium rapidly in the first week, which is what produces the headaches and fatigue known as keto flu. Most people need 3,000-5,000 mg of sodium, 1,000-3,500 mg of potassium and 300-500 mg of magnesium daily during adaptation." },
    ],
    staticTable: {
      title: "Net carb targets by keto tier",
      head: ["Tier", "Net carbs / day", "Typical ketone range (mmol/L)", "Best suited to"],
      rows: [
        ["Therapeutic keto", "under 20 g", "1.5-3.0", "Epilepsy protocols, deep adaptation"],
        ["Standard keto", "20-30 g", "0.8-2.0", "Fat loss, appetite control"],
        ["Moderate keto", "30-50 g", "0.5-1.2", "Maintenance, active adults"],
        ["Low carb", "50-100 g", "under 0.5", "Athletes, glycogen-dependent sports"],
        ["Cyclical keto", "20 g + refeed", "variable", "Advanced lifters"],
      ],
    },
    faqs: [
      { q: "Should I count net carbs or total carbs on keto?", a: "Net carbs are the practical target because fibre and erythritol do not raise blood glucose. If your weight loss stalls despite low net carbs, switching to total carbs for two weeks usually exposes hidden sugar alcohols." },
      { q: "How much fat should I eat per day on keto?", a: "Fat is the balancing macro. Once protein and net carbs are fixed, fat fills whatever calories remain — around 55-75% of intake for most people. Eating more fat than that surplus does not deepen ketosis, it just adds calories." },
      { q: "Do sugar alcohols kick you out of ketosis?", a: "Erythritol and allulose are essentially inert. Maltitol has a glycaemic index near 35 and can measurably raise glucose, so half its grams should be counted." },
    ],
    related: ["tdee-calculator-weight-loss", "calorie-deficit-target-date-calculator", "water-fasting-weight-loss-calculator", "ideal-body-weight-calculator"],
  },

  {
    slug: "water-fasting-weight-loss-calculator",
    cluster: "fitness",
    name: "Water Fasting Weight Loss Projection",
    h1: "Water Fasting Weight Loss Projection Calculator (Fat vs Water Split)",
    title: "Water Fasting Weight Loss Projection Calculator",
    description:
      "Project weight lost during a water fast, split into glycogen water, true fat loss and lean tissue. Includes autophagy timeline, refeed guidance and per-day loss table.",
    keywords: [
      "water fasting weight loss projection calculator",
      "how much weight can you lose in a 3 day water fast",
      "72 hour fast fat loss vs water weight",
      "autophagy timeline hours fasting",
    ],
    inputs: [
      { id: "weight", label: "Starting weight", type: "number", def: "85", unit: "kg", min: 35 },
      { id: "days", label: "Fast length", type: "number", def: "3", unit: "days", min: 1, max: 14 },
      { id: "activity", label: "Activity during fast", type: "select", def: "1.2", options: [
        { value: "1.1", label: "Bed rest" },
        { value: "1.2", label: "Light — desk work, walking" },
        { value: "1.35", label: "Moderate — on your feet" },
      ] },
      { id: "gender", label: "Gender", type: "select", def: "male", options: [{ value: "male", label: "Male" }, { value: "female", label: "Female" }] },
      { id: "age", label: "Age", type: "number", def: "35", unit: "years", min: 18, max: 80 },
      { id: "height", label: "Height", type: "number", def: "175", unit: "cm", min: 120 },
    ],
    compute: (v) => {
      const kg = n(v, "weight"), days = n(v, "days", 1);
      const bmr = 10 * kg + 6.25 * n(v, "height") - 5 * n(v, "age") + (s(v, "gender") === "male" ? 5 : -161);
      const burn = bmr * n(v, "activity", 1.2);
      const totalKcal = burn * days;
      const fatKg = (totalKcal * 0.85) / 7700;
      const leanKg = (totalKcal * 0.15) / 7000;
      const glycogenWater = Math.min(2.2, 0.4 + days * 0.5);
      const scaleLoss = fatKg + leanKg + glycogenWater;
      return {
        cards: [
          { label: "Scale weight lost", value: `${fmt(scaleLoss, 2)} kg`, sub: `${fmt(scaleLoss * 2.205, 1)} lb on the scale`, accent: true },
          { label: "True fat loss", value: `${fmt(fatKg, 2)} kg`, sub: "Regain-proof portion" },
          { label: "Glycogen water", value: `${fmt(glycogenWater, 2)} kg`, sub: "Returns within 48h of refeed" },
          { label: "Daily burn", value: `${fmt(burn)} kcal`, sub: `${fmt(totalKcal)} kcal over ${days} day(s)` },
        ],
        bars: [
          { label: "Fat", value: fatKg, display: `${fmt(fatKg, 2)} kg` },
          { label: "Water", value: glycogenWater, display: `${fmt(glycogenWater, 2)} kg` },
          { label: "Lean", value: leanKg, display: `${fmt(leanKg, 2)} kg` },
        ],
        summary: `Only about ${fmt((fatKg / scaleLoss) * 100)}% of the scale drop is fat — the rest returns once glycogen refills.`,
      };
    },
    intro:
      "Every gram of stored glycogen binds roughly three grams of water, so the first two days of a water fast produce a dramatic scale drop that has almost nothing to do with fat. This projection separates the three components — bound water, oxidised fat and the small lean-tissue contribution — so the number you see on day four does not mislead you after the refeed.",
    sections: [
      { h: "What happens hour by hour", body: "Blood glucose falls for the first 12 hours while liver glycogen supplies the brain. Between 12 and 36 hours glycogen depletes and hepatic gluconeogenesis ramps up, drawing on glycerol and amino acids. From roughly 48 hours ketone production dominates, protein sparing improves, and measurable autophagy markers rise. Growth hormone climbs steadily and peaks around day three." },
      { h: "Why lean loss is not zero", body: "Even in deep ketosis the body converts some amino acids to glucose for red blood cells and renal medulla. Modelling puts this near 15% of the energy deficit in the first three days, falling below 5% in adapted multi-day fasts, which is why extended fasts are less catabolic per day than short repeated ones." },
      { h: "Refeeding without rebounding", body: "Break a fast longer than 48 hours with a small protein and fat meal, not carbohydrate. Rapid carbohydrate loading after several days without food shifts phosphate, potassium and magnesium into cells and can precipitate refeeding syndrome. Ramp to normal intake over two to three days." },
    ],
    staticTable: {
      title: "Typical scale loss during a water fast (80 kg adult)",
      head: ["Fast length", "Scale loss", "Of which water", "Of which fat", "Dominant fuel"],
      rows: [
        ["24 hours", "1.2-1.6 kg", "~0.9 kg", "~0.25 kg", "Liver glycogen"],
        ["48 hours", "2.2-2.8 kg", "~1.4 kg", "~0.55 kg", "Gluconeogenesis"],
        ["72 hours", "3.0-3.8 kg", "~1.7 kg", "~0.85 kg", "Ketones"],
        ["5 days", "4.5-5.5 kg", "~2.0 kg", "~1.4 kg", "Ketones + fat"],
        ["7 days", "6.0-7.0 kg", "~2.2 kg", "~2.0 kg", "Deep ketosis"],
      ],
    },
    faqs: [
      { q: "How much of water fasting weight loss is actually fat?", a: "Roughly 25-35% on a three-day fast and up to 60% on a seven-day fast. The remainder is glycogen-bound water and gut content, which returns within a day or two of eating normally." },
      { q: "Is a multi-day water fast safe?", a: "Fasts beyond 48-72 hours should be medically supervised, especially with diabetes, low body weight, pregnancy, or any medication that lowers blood pressure or glucose. Electrolyte depletion, not hunger, is the main risk." },
      { q: "Will fasting slow my metabolism?", a: "Short fasts up to 72 hours generally maintain or slightly raise resting expenditure due to catecholamine release. Sustained repeated fasting with heavy calorie restriction is what drives adaptive downregulation." },
    ],
    related: ["fasting-electrolyte-calculator", "tdee-calculator-weight-loss", "calorie-deficit-target-date-calculator", "keto-net-carb-calculator"],
  },

  {
    slug: "caffeine-half-life-calculator",
    cluster: "fitness",
    name: "Caffeine Half-Life & Sleep Safety Timer",
    h1: "Caffeine Half-Life Calculator (Sleep Safety Cut-Off Timer)",
    title: "Caffeine Half-Life Calculator & Sleep Safety Timer",
    description:
      "See how much caffeine is still in your bloodstream at bedtime. Enter your drink, the time you had it and your metabolism speed to get an hour-by-hour decay curve.",
    keywords: [
      "caffeine half life calculator sleep safety timer",
      "how long does caffeine stay in your system before bed",
      "caffeine cut off time for 11pm bedtime",
      "mg of caffeine left after 6 hours",
    ],
    inputs: [
      { id: "drink", label: "Drink", type: "select", def: "95", options: [
        { value: "95", label: "Brewed coffee (240 ml) — 95 mg" },
        { value: "63", label: "Espresso shot — 63 mg" },
        { value: "150", label: "Large takeaway coffee — 150 mg" },
        { value: "80", label: "Energy drink (250 ml) — 80 mg" },
        { value: "160", label: "Large energy drink (500 ml) — 160 mg" },
        { value: "47", label: "Black tea (240 ml) — 47 mg" },
        { value: "34", label: "Cola (355 ml) — 34 mg" },
        { value: "200", label: "Pre-workout scoop — 200 mg" },
      ] },
      { id: "servings", label: "Servings consumed", type: "number", def: "2", min: 1, max: 10 },
      { id: "time", label: "Time consumed", type: "time", def: "14:00" },
      { id: "bedtime", label: "Planned bedtime", type: "time", def: "23:00" },
      { id: "halflife", label: "Your metabolism", type: "select", def: "5", options: [
        { value: "3.5", label: "Fast — CYP1A2 rapid metaboliser" },
        { value: "5", label: "Average — 5 hour half-life" },
        { value: "7", label: "Slow — smoker-free, low CYP1A2" },
        { value: "9", label: "Very slow — oral contraceptives / pregnancy" },
      ] },
    ],
    compute: (v) => {
      const dose = n(v, "drink", 95) * n(v, "servings", 1);
      const hl = n(v, "halflife", 5);
      const [th, tm] = s(v, "time", "14:00").split(":").map(Number);
      const [bh, bm] = s(v, "bedtime", "23:00").split(":").map(Number);
      let hours = (bh * 60 + bm - (th * 60 + tm)) / 60;
      if (hours < 0) hours += 24;
      const atBed = dose * Math.pow(0.5, hours / hl);
      const hoursTo50 = hl * Math.log2(dose / 50);
      const cutoff = new Date();
      cutoff.setHours(bh, bm, 0, 0);
      cutoff.setTime(cutoff.getTime() - hl * Math.log2(dose / 50) * 3600000);
      const rows: (string | number)[][] = [];
      for (let h = 0; h <= 12; h += 2) {
        const d = new Date();
        d.setHours(th, tm, 0, 0);
        d.setTime(d.getTime() + h * 3600000);
        rows.push([`+${h} h`, d.toTimeString().slice(0, 5), `${fmt(dose * Math.pow(0.5, h / hl))} mg`]);
      }
      return {
        cards: [
          { label: "Caffeine at bedtime", value: `${fmt(atBed)} mg`, sub: atBed > 100 ? "High — sleep onset likely delayed" : atBed > 50 ? "Moderate — lighter deep sleep" : "Low — should not affect sleep", accent: true },
          { label: "Dose taken", value: `${fmt(dose)} mg`, sub: `${n(v, "servings", 1)} serving(s)` },
          { label: "Half-life used", value: `${hl} h`, sub: "Time to halve the dose" },
          { label: "Latest safe intake", value: cutoff.toTimeString().slice(0, 5), sub: "To be under 50 mg at bedtime" },
        ],
        table: { title: "Decay curve from the time you drank it", head: ["Elapsed", "Clock time", "Caffeine remaining"], rows },
        summary: `It takes about ${fmt(hoursTo50, 1)} hours for ${fmt(dose)} mg to fall below the 50 mg sleep-safe threshold.`,
      };
    },
    intro:
      "Caffeine clears by first-order kinetics: a fixed fraction leaves your bloodstream per unit of time, not a fixed number of milligrams. With an average half-life of five hours, a 200 mg afternoon coffee still leaves 50 mg circulating ten hours later — enough adenosine blockade to measurably cut slow-wave sleep even if you fall asleep on time.",
    sections: [
      { h: "Why half-life varies so much between people", body: "The CYP1A2 liver enzyme does about 95% of caffeine clearance, and its activity is genetically variable. Smoking roughly doubles clearance; oral contraceptives, pregnancy and some antibiotics halve it. That is why one person sleeps after an espresso at 9 pm and another lies awake from a 2 pm cup." },
      { h: "The 50 mg sleep threshold", body: "Sleep laboratory work shows measurable reductions in deep sleep at circulating levels above roughly 50 mg. The calculator therefore back-solves your latest safe intake time from the bedtime you enter rather than repeating the generic 'no caffeine after 2 pm' advice." },
      { h: "Tolerance is not clearance", body: "Regular use up-regulates adenosine receptors, so subjective alertness fades — but the drug still occupies receptors during the night. That is the trap of habitual heavy intake: you stop feeling wired while sleep architecture keeps degrading." },
    ],
    staticTable: {
      title: "Caffeine content of common drinks",
      head: ["Drink", "Serving", "Caffeine", "Hours to fall under 50 mg (5 h half-life)"],
      rows: [
        ["Brewed coffee", "240 ml", "95 mg", "4.6 h"],
        ["Espresso", "30 ml", "63 mg", "1.7 h"],
        ["Instant coffee", "240 ml", "62 mg", "1.6 h"],
        ["Energy drink", "250 ml", "80 mg", "3.4 h"],
        ["Pre-workout", "1 scoop", "200 mg", "10.0 h"],
        ["Black tea", "240 ml", "47 mg", "0 h"],
        ["Green tea", "240 ml", "28 mg", "0 h"],
        ["Cola", "355 ml", "34 mg", "0 h"],
      ],
    },
    faqs: [
      { q: "How long before bed should I stop drinking coffee?", a: "For an average metaboliser, at least 8-10 hours before bed for a 200 mg dose. The calculator above solves this exactly for your dose, bedtime and metabolism speed instead of using a blanket rule." },
      { q: "Does caffeine fully leave your system overnight?", a: "Not always. With a 9-hour half-life, 300 mg taken at noon still leaves about 40 mg at 7 am the next morning, which contributes to the cycle of needing coffee to feel awake." },
      { q: "Is the quarter-life more important than the half-life?", a: "For sleep, yes. Falling to 25% of the dose takes two half-lives, and that is usually the point at which sleep architecture is no longer measurably affected." },
    ],
    related: ["sleep-cycle-rem-calculator", "eye-strain-20-20-20-timer", "coffee-to-water-ratio-calculator", "target-heart-rate-zone-calculator"],
  },

  {
    slug: "calorie-deficit-target-date-calculator",
    cluster: "fitness",
    name: "Calorie Deficit Target Date",
    h1: "Calorie Deficit Calculator with Target Weight Loss Date",
    title: "Calorie Deficit Calculator — Target Weight Loss Date",
    description:
      "Enter current weight, goal weight and daily deficit to get the exact date you reach target, weekly milestone table and a plateau-adjusted realistic date.",
    keywords: [
      "calorie deficit calculator target weight loss date",
      "how long to lose 10 kg at 500 calorie deficit",
      "weight loss date predictor by deficit",
      "realistic weight loss timeline calculator",
    ],
    inputs: [
      { id: "current", label: "Current weight", type: "number", def: "90", unit: "kg", min: 30 },
      { id: "goal", label: "Goal weight", type: "number", def: "78", unit: "kg", min: 30 },
      { id: "deficit", label: "Daily calorie deficit", type: "number", def: "500", unit: "kcal", min: 100, max: 1500, step: 50 },
      { id: "adherence", label: "Realistic adherence", type: "select", def: "0.85", options: [
        { value: "1", label: "Perfect — 7/7 days on plan" },
        { value: "0.85", label: "Good — one loose day a week" },
        { value: "0.7", label: "Average — weekends off plan" },
      ] },
    ],
    compute: (v) => {
      const lose = Math.max(0, n(v, "current") - n(v, "goal"));
      const eff = n(v, "deficit") * n(v, "adherence", 1);
      const weeklyKg = (eff * 7) / 7700;
      const weeks = weeklyKg > 0 ? lose / weeklyKg : 0;
      const idealWeeks = lose / ((n(v, "deficit") * 7) / 7700);
      const date = new Date(Date.now() + weeks * 7 * 86400000);
      const rows: (string | number)[][] = [];
      for (let m = 1; m <= 6; m++) {
        const w = Math.min(lose, weeklyKg * 4.345 * m);
        rows.push([`Month ${m}`, `${fmt(n(v, "current") - w, 1)} kg`, `−${fmt(w, 1)} kg`, `${fmt((w / Math.max(lose, 0.01)) * 100)}%`]);
      }
      return {
        cards: [
          { label: "Target date", value: weeks > 0 ? date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "Goal reached", sub: `${fmt(weeks, 1)} weeks at your adherence`, accent: true },
          { label: "Weekly loss", value: `${fmt(weeklyKg, 2)} kg`, sub: `${fmt(weeklyKg * 2.205, 2)} lb per week` },
          { label: "Total to lose", value: `${fmt(lose, 1)} kg`, sub: `${fmt(lose * 2.205, 1)} lb` },
          { label: "Perfect-adherence date", value: `${fmt(idealWeeks, 1)} wks`, sub: "If you never miss a day" },
        ],
        table: { title: "Six-month milestone projection", head: ["Milestone", "Projected weight", "Lost", "% of goal"], rows },
        summary: `Losing ${fmt(lose, 1)} kg at ${fmt(eff)} effective kcal/day takes about ${fmt(weeks / 4.345, 1)} months.`,
      };
    },
    intro:
      "The 7,700 kcal per kilogram rule (3,500 kcal per pound) is a linear approximation of a non-linear process. It holds well for the first 10-12% of body weight lost, then over-predicts because a smaller body costs less to move and maintain. This calculator therefore prints both the arithmetic date and an adherence-weighted realistic date.",
    sections: [
      { h: "Why your deficit shrinks as you shrink", body: "Every kilogram lost removes roughly 20-25 kcal a day of maintenance requirement. Losing 10 kg silently converts a 500 kcal deficit into a 280 kcal deficit unless you recalculate. Reweigh and recalculate every four to six weeks." },
      { h: "Adherence beats aggression", body: "A 300 kcal deficit followed seven days a week outperforms a 700 kcal deficit followed four days a week — the second averages to less total energy removed and carries much higher binge risk. The adherence selector models this directly by scaling your effective daily deficit." },
      { h: "Reading the weekly scale honestly", body: "Daily fluctuations of 1-2 kg from sodium, carbohydrate, menstrual cycle and gut content routinely mask fat loss of 0.15 kg a day. Weigh at the same time each morning and compare seven-day rolling averages, never single readings." },
    ],
    staticTable: {
      title: "Time to lose weight at common deficits",
      head: ["Weight to lose", "250 kcal/day", "500 kcal/day", "750 kcal/day", "1000 kcal/day"],
      rows: [
        ["5 kg", "44 weeks", "22 weeks", "15 weeks", "11 weeks"],
        ["10 kg", "88 weeks", "44 weeks", "29 weeks", "22 weeks"],
        ["15 kg", "132 weeks", "66 weeks", "44 weeks", "33 weeks"],
        ["20 kg", "176 weeks", "88 weeks", "59 weeks", "44 weeks"],
        ["30 kg", "264 weeks", "132 weeks", "88 weeks", "66 weeks"],
      ],
      caption: "Arithmetic projection before metabolic adaptation is applied.",
    },
    faqs: [
      { q: "How accurate is the 3,500 calorie per pound rule?", a: "It is accurate enough for planning the first few months. Over longer horizons it over-predicts loss by 10-25% because maintenance calories fall as body mass falls." },
      { q: "What is the biggest deficit I should run?", a: "Most practitioners cap it at 20-25% below maintenance, or about 1% of body weight lost per week, to protect lean mass, training quality and hormonal function." },
      { q: "Why did my weight loss stall completely?", a: "The most common causes are unmeasured portion creep, reduced spontaneous movement, and a deficit that has shrunk as you lost weight. Recalculate maintenance at your new weight before cutting further." },
    ],
    related: ["tdee-calculator-weight-loss", "ideal-body-weight-calculator", "keto-net-carb-calculator", "water-fasting-weight-loss-calculator"],
  },

  {
    slug: "ideal-body-weight-calculator",
    cluster: "fitness",
    name: "Ideal Weight (Multi-Formula)",
    h1: "Ideal Body Weight Calculator — Devine, Robinson, Miller & Hamwi",
    title: "Ideal Body Weight Calculator (Devine, Robinson, Miller)",
    description:
      "Compare four clinical ideal body weight formulas side by side, adjusted for frame size, plus healthy BMI range and drug-dosing lean body weight.",
    keywords: [
      "ideal body weight calculator devine robinson miller",
      "ideal weight for height and frame size chart",
      "hamwi formula ideal body weight",
      "healthy weight range by bmi and height",
    ],
    inputs: [
      { id: "gender", label: "Gender", type: "select", def: "male", options: [{ value: "male", label: "Male" }, { value: "female", label: "Female" }] },
      { id: "height", label: "Height", type: "number", def: "175", unit: "cm", min: 120, max: 220 },
      { id: "frame", label: "Frame size", type: "select", def: "1", options: [
        { value: "0.9", label: "Small frame (−10%)" },
        { value: "1", label: "Medium frame" },
        { value: "1.1", label: "Large frame (+10%)" },
      ] },
    ],
    compute: (v) => {
      const cm = n(v, "height"), male = s(v, "gender") === "male", f = n(v, "frame", 1);
      const over = Math.max(0, cm - 152.4) / 2.54;
      const devine = (male ? 50 : 45.5) + 2.3 * over;
      const robinson = (male ? 52 : 49) + (male ? 1.9 : 1.7) * over;
      const miller = (male ? 56.2 : 53.1) + (male ? 1.41 : 1.36) * over;
      const hamwi = (male ? 48 : 45.5) + (male ? 2.7 : 2.2) * over;
      const m = cm / 100;
      const avg = ((devine + robinson + miller + hamwi) / 4) * f;
      return {
        cards: [
          { label: "Ideal weight", value: `${fmt(avg, 1)} kg`, sub: `${fmt(avg * 2.205, 1)} lb — 4-formula average`, accent: true },
          { label: "Devine (1974)", value: `${fmt(devine * f, 1)} kg`, sub: "Drug dosing standard" },
          { label: "Robinson (1983)", value: `${fmt(robinson * f, 1)} kg`, sub: "Lower, modern revision" },
          { label: "Healthy BMI range", value: `${fmt(18.5 * m * m, 1)}-${fmt(24.9 * m * m, 1)} kg`, sub: "WHO band for your height" },
        ],
        bars: [
          { label: "Devine", value: devine * f, display: `${fmt(devine * f, 1)} kg` },
          { label: "Robinson", value: robinson * f, display: `${fmt(robinson * f, 1)} kg` },
          { label: "Miller", value: miller * f, display: `${fmt(miller * f, 1)} kg` },
          { label: "Hamwi", value: hamwi * f, display: `${fmt(hamwi * f, 1)} kg` },
        ],
        summary: `Frame adjustment applied: ${f === 1 ? "none (medium frame)" : f < 1 ? "−10% for a small frame" : "+10% for a large frame"}.`,
      };
    },
    intro:
      "Ideal body weight formulas were never designed for aesthetics — Devine's equation was written in 1974 to standardise gentamicin dosing, and the rest are refinements of the same idea. They give a clinically useful single number for a given height, but they ignore muscle mass entirely, which is why they are printed alongside the WHO healthy BMI band here.",
    sections: [
      { h: "How the four formulas differ", body: "All four use the same shape: a base weight at 5 feet plus a fixed increment per inch above it. Hamwi is the most generous per inch (2.7 kg for men), Miller the most conservative (1.41 kg), with Devine and Robinson between them. At 175 cm the spread between the highest and lowest result is typically 6-8 kg." },
      { h: "Frame size actually matters", body: "Elbow breadth and wrist circumference correlate with skeletal mass. A wrist under 16.5 cm in a 175 cm man indicates a small frame, over 19 cm a large one. Applying a ±10% adjustment brings the formulas much closer to measured healthy weights at the extremes of build." },
      { h: "When to ignore the number entirely", body: "Trained lifters, rugby forwards and sprinters routinely exceed every ideal weight formula while carrying 10% body fat. Conversely a sedentary person can sit exactly on Devine weight with 32% fat. Pair this tool with a body fat estimate before drawing conclusions." },
    ],
    staticTable: {
      title: "Ideal weight by height (medium frame)",
      head: ["Height", "Male Devine", "Male Robinson", "Female Devine", "Female Robinson", "Healthy BMI range"],
      rows: [
        ["155 cm", "52.4 kg", "53.0 kg", "47.9 kg", "50.0 kg", "44-60 kg"],
        ["160 cm", "57.0 kg", "56.8 kg", "52.5 kg", "53.4 kg", "47-64 kg"],
        ["165 cm", "61.5 kg", "60.5 kg", "57.0 kg", "56.8 kg", "50-68 kg"],
        ["170 cm", "66.1 kg", "64.3 kg", "61.6 kg", "60.1 kg", "53-72 kg"],
        ["175 cm", "70.7 kg", "68.1 kg", "66.2 kg", "63.5 kg", "57-76 kg"],
        ["180 cm", "75.3 kg", "71.9 kg", "70.8 kg", "66.9 kg", "60-81 kg"],
        ["185 cm", "79.9 kg", "75.7 kg", "75.4 kg", "70.3 kg", "63-85 kg"],
      ],
    },
    faqs: [
      { q: "Which ideal body weight formula do hospitals use?", a: "Devine remains the default for drug dosing and ventilator tidal-volume settings. Robinson and Miller are more common in nutrition literature because they produce lower, more realistic targets." },
      { q: "How do I know my frame size?", a: "Wrap your thumb and middle finger around the opposite wrist. Overlap indicates a small frame, touching a medium frame, and a gap a large frame. Elbow breadth measured with callipers is the clinical version." },
      { q: "Is ideal body weight the same as a healthy weight?", a: "No. Healthy weight is a range — the BMI 18.5-24.9 band spans 19 kg at 175 cm. Ideal body weight is a single reference point inside that range." },
    ],
    related: ["tdee-calculator-weight-loss", "us-navy-body-fat-calculator", "calorie-deficit-target-date-calculator", "target-heart-rate-zone-calculator"],
  },

  {
    slug: "target-heart-rate-zone-calculator",
    cluster: "fitness",
    name: "Heart Rate Zone Estimator",
    h1: "Target Heart Rate Zone Calculator (Karvonen Fat Burn & Cardio Zones)",
    title: "Target Heart Rate Zone Calculator — Karvonen Method",
    description:
      "Calculate all five training heart rate zones using the Karvonen heart rate reserve method, with fat-burn vs cardio comparison and a resting-HR fitness benchmark.",
    keywords: [
      "target heart rate zone calculator fat burn cardio",
      "karvonen formula heart rate reserve zones",
      "max heart rate by age tanaka formula",
      "zone 2 training heart rate calculator",
    ],
    inputs: [
      { id: "age", label: "Age", type: "number", def: "32", unit: "years", min: 10, max: 95 },
      { id: "resting", label: "Resting heart rate", type: "number", def: "62", unit: "bpm", min: 30, max: 120, help: "Measure on waking, before getting out of bed." },
      { id: "formula", label: "Max HR formula", type: "select", def: "tanaka", options: [
        { value: "tanaka", label: "Tanaka — 208 − 0.7 × age" },
        { value: "haskell", label: "Haskell — 220 − age" },
        { value: "gulati", label: "Gulati (women) — 206 − 0.88 × age" },
      ] },
    ],
    compute: (v) => {
      const age = n(v, "age"), rhr = n(v, "resting");
      const f = s(v, "formula");
      const mhr = f === "haskell" ? 220 - age : f === "gulati" ? 206 - 0.88 * age : 208 - 0.7 * age;
      const hrr = mhr - rhr;
      const z = (lo: number, hi: number) => `${Math.round(rhr + hrr * lo)}-${Math.round(rhr + hrr * hi)} bpm`;
      return {
        cards: [
          { label: "Max heart rate", value: `${fmt(mhr)} bpm`, sub: f === "tanaka" ? "Tanaka formula" : f === "gulati" ? "Gulati (female-specific)" : "Haskell 220 − age", accent: true },
          { label: "Heart rate reserve", value: `${fmt(hrr)} bpm`, sub: "Max minus resting" },
          { label: "Zone 2 (fat burn)", value: z(0.6, 0.7), sub: "Conversational aerobic base" },
          { label: "Zone 4 (threshold)", value: z(0.8, 0.9), sub: "Lactate threshold work" },
        ],
        table: {
          title: "Your five Karvonen training zones",
          head: ["Zone", "% of HR reserve", "Your range", "Purpose", "Typical duration"],
          rows: [
            ["Zone 1 — Recovery", "50-60%", z(0.5, 0.6), "Active recovery, warm-up", "20-40 min"],
            ["Zone 2 — Fat burn", "60-70%", z(0.6, 0.7), "Aerobic base, fat oxidation", "45-120 min"],
            ["Zone 3 — Aerobic", "70-80%", z(0.7, 0.8), "Cardiovascular efficiency", "20-60 min"],
            ["Zone 4 — Threshold", "80-90%", z(0.8, 0.9), "Lactate clearance, race pace", "10-30 min"],
            ["Zone 5 — VO2 max", "90-100%", z(0.9, 1), "Peak power, intervals", "1-8 min total"],
          ],
        },
        summary: `A resting rate of ${rhr} bpm gives you a ${fmt(hrr)} bpm working reserve — the wider this is, the fitter your heart.`,
      };
    },
    intro:
      "The Karvonen method calculates zones as a percentage of heart rate reserve — the gap between resting and maximum heart rate — rather than as a raw percentage of maximum. This matters because two runners with the same maximum but resting rates of 48 and 72 bpm need very different absolute paces to sit in the same physiological zone.",
    sections: [
      { h: "Percentage of max vs heart rate reserve", body: "The simple method (70% of max HR) ignores your baseline entirely and typically prescribes zones 8-12 bpm too low for well-trained athletes and slightly too high for deconditioned beginners. Karvonen anchors both ends of the scale to you personally, so Zone 2 stays genuinely conversational." },
      { h: "Which max HR formula to trust", body: "220 minus age (Haskell) was never derived from controlled data and has a standard deviation of around 12 bpm. Tanaka's 208 − 0.7 × age fits large meta-analytic samples better, especially over 40, and Gulati's equation was validated specifically in women. A supervised ramp test still beats every formula." },
      { h: "Why the fat-burn zone is misunderstood", body: "Zone 2 oxidises the highest proportion of fat, but higher intensities burn more total fat per minute because total energy cost rises. Zone 2 earns its place through mitochondrial density and capillarisation, not through the ratio printed on gym treadmills." },
    ],
    staticTable: {
      title: "Estimated max heart rate and Zone 2 by age",
      head: ["Age", "Tanaka max HR", "Haskell max HR", "Zone 2 (RHR 60)", "Zone 5 (RHR 60)"],
      rows: [
        [20, 194, 200, "140-154 bpm", "181-194 bpm"],
        [30, 187, 190, "136-149 bpm", "175-187 bpm"],
        [40, 180, 180, "132-144 bpm", "168-180 bpm"],
        [50, 173, 170, "128-139 bpm", "162-173 bpm"],
        [60, 166, 160, "124-134 bpm", "155-166 bpm"],
        [70, 159, 150, "119-129 bpm", "149-159 bpm"],
      ],
    },
    faqs: [
      { q: "What heart rate is the fat burning zone?", a: "Roughly 60-70% of heart rate reserve. For a 32-year-old with a resting rate of 62 bpm, that is about 137-153 bpm — an effort where you can still hold a full conversation." },
      { q: "Is 220 minus age accurate?", a: "It is a rough guide with an error margin of about ±12 bpm. Tanaka's formula fits population data better, and a field or lab ramp test is more accurate than any equation." },
      { q: "How do I measure resting heart rate properly?", a: "Take it immediately on waking, lying still, for a full 60 seconds — or read the overnight average from a wearable. Caffeine, alcohol, illness and poor sleep all raise it by 5-10 bpm." },
    ],
    related: ["tdee-calculator-weight-loss", "caffeine-half-life-calculator", "one-rep-max-calculator", "sleep-cycle-rem-calculator"],
  },

  {
    slug: "sourdough-hydration-calculator",
    cluster: "fitness",
    name: "Sourdough & Baker's Hydration",
    h1: "Sourdough Hydration Calculator (Baker's Percentage with Starter)",
    title: "Sourdough Hydration Calculator — Baker's Percentage",
    description:
      "Calculate true dough hydration including the flour and water inside your starter, plus salt percentage, total dough weight and a hydration-to-crumb reference table.",
    keywords: [
      "sourdough hydration calculator baker percentage",
      "true hydration including starter formula",
      "how much salt per 500g flour sourdough",
      "75 percent hydration dough water calculation",
    ],
    inputs: [
      { id: "flour", label: "Flour in the dough", type: "number", def: "500", unit: "g", min: 50 },
      { id: "water", label: "Water in the dough", type: "number", def: "350", unit: "g", min: 10 },
      { id: "starter", label: "Starter / levain", type: "number", def: "100", unit: "g", min: 0 },
      { id: "starterhyd", label: "Starter hydration", type: "select", def: "100", options: [
        { value: "100", label: "100% — equal flour and water" },
        { value: "80", label: "80% — stiffer starter" },
        { value: "50", label: "50% — stiff levain / lievito madre" },
        { value: "125", label: "125% — liquid levain" },
      ] },
      { id: "saltpct", label: "Salt percentage", type: "number", def: "2", unit: "%", min: 0, max: 4, step: 0.1 },
    ],
    compute: (v) => {
      const flour = n(v, "flour"), water = n(v, "water"), st = n(v, "starter"), sh = n(v, "starterhyd", 100);
      const stFlour = st / (1 + sh / 100);
      const stWater = st - stFlour;
      const totalFlour = flour + stFlour;
      const totalWater = water + stWater;
      const hyd = totalFlour ? (totalWater / totalFlour) * 100 : 0;
      const salt = (totalFlour * n(v, "saltpct", 2)) / 100;
      const total = totalFlour + totalWater + salt;
      return {
        cards: [
          { label: "True hydration", value: `${fmt(hyd, 1)}%`, sub: hyd < 65 ? "Stiff — sandwich crumb" : hyd < 75 ? "Standard artisan" : hyd < 85 ? "High — open crumb" : "Very high — ciabatta territory", accent: true },
          { label: "Total flour", value: `${fmt(totalFlour)} g`, sub: `${fmt(stFlour)} g from starter` },
          { label: "Total water", value: `${fmt(totalWater)} g`, sub: `${fmt(stWater)} g from starter` },
          { label: "Salt to add", value: `${fmt(salt, 1)} g`, sub: `${n(v, "saltpct", 2)}% of total flour` },
        ],
        table: {
          title: "Your full baker's percentage formula",
          head: ["Ingredient", "Weight", "Baker's %"],
          rows: [
            ["Flour (dough)", `${fmt(flour)} g`, `${fmt((flour / totalFlour) * 100, 1)}%`],
            ["Flour (from starter)", `${fmt(stFlour)} g`, `${fmt((stFlour / totalFlour) * 100, 1)}%`],
            ["Water (total)", `${fmt(totalWater)} g`, `${fmt(hyd, 1)}%`],
            ["Starter", `${fmt(st)} g`, `${fmt((st / totalFlour) * 100, 1)}%`],
            ["Salt", `${fmt(salt, 1)} g`, `${n(v, "saltpct", 2)}%`],
            ["Total dough", `${fmt(total)} g`, "—"],
          ],
        },
        summary: `Ignoring the starter would report ${fmt((water / flour) * 100, 1)}% hydration — ${fmt(Math.abs(hyd - (water / flour) * 100), 1)} points off the true figure.`,
      };
    },
    intro:
      "Baker's percentage expresses every ingredient as a share of total flour weight, which is why two bakers with different batch sizes can share one formula. The mistake almost everyone makes is excluding the starter: 100 g of 100% hydration levain adds 50 g of flour and 50 g of water, which shifts a supposed 70% dough to a real 71.7%.",
    sections: [
      { h: "How hydration changes the crumb", body: "Below 65% you get a tight, sliceable sandwich crumb and a dough that shapes easily. Between 70 and 78% the gluten network holds larger irregular alveoli — the classic artisan look. Above 82% the dough becomes slack, needs coil folds rather than kneading, and bakes best in a covered pot to trap steam." },
      { h: "Salt is not optional", body: "At 1.8-2.2% of total flour, salt tightens gluten, slows fermentation and controls enzymatic activity. Drop below 1.5% and the dough ferments too fast and tastes flat; above 2.5% yeast activity is measurably suppressed and proofing times stretch out." },
      { h: "Adjusting for flour type", body: "Whole wheat absorbs roughly 8-10% more water than white bread flour, and rye more still. If you swap 20% of white for whole grain, add about 15-20 g extra water per 500 g of flour or the dough will feel much stiffer than the percentage suggests." },
    ],
    staticTable: {
      title: "Hydration levels and what they produce",
      head: ["Hydration", "Dough feel", "Crumb", "Best method", "Typical bread"],
      rows: [
        ["60-65%", "Firm, easy to knead", "Tight, even", "Machine or hand knead", "Sandwich loaf, bagels"],
        ["66-70%", "Slightly tacky", "Semi-open", "Kneading + one fold", "Boule, batard"],
        ["71-78%", "Sticky, extensible", "Open, irregular", "Stretch and fold", "Country sourdough"],
        ["79-85%", "Slack, wet", "Very open", "Coil folds, banneton", "Pain rustique"],
        ["86-100%", "Pourable", "Huge holes", "No-knead, high heat", "Ciabatta, focaccia"],
      ],
    },
    faqs: [
      { q: "How do I calculate true hydration including starter?", a: "Split the starter into its flour and water halves according to its own hydration, add each half to the dough totals, then divide total water by total flour. A 100% starter splits 50/50 by weight." },
      { q: "What is the best hydration for a beginner?", a: "Around 68-70%. It is forgiving to shape, still produces a pleasantly open crumb, and does not require advanced folding technique." },
      { q: "Does starter percentage change proof time?", a: "Yes. Doubling the levain from 10% to 20% of flour roughly halves bulk fermentation time at the same temperature." },
    ],
    related: ["bakers-percentage-recipe-calculator", "coffee-to-water-ratio-calculator", "recipe-scale-converter", "keto-net-carb-calculator"],
  },
];